// Property rental income prediction service
export interface PropertyPrediction {
  estimatedMonthlyIncome: number;
  estimatedYearlyIncome: number;
  confidence: 'low' | 'medium' | 'high';
  factors: {
    location: number;
    propertyType: number;
    furnishing: number;
    bedrooms: number;
    bathrooms: number;
  };
  recommendations: string[];
  marketInsights: {
    averageRentalRate: number;
    occupancyRate: number;
    seasonalVariation: number;
  };
}

export interface PropertyInputs {
  area: string;
  propertyType: string;
  furnishStatus: string;
  selectedBeds: string[];
  selectedBaths: string[];
  name?: string;
  mobile?: string;
  email?: string;
  propertyName?: string;
}

// Dubai area rental rates (AED per month) - base rates for 1 bedroom
const AREA_RATES: { [key: string]: number } = {
  'downtown': 12000,
  'dubai marina': 10000,
  'palm jumeirah': 15000,
  'business bay': 8000,
  'jbr': 9000,
  'dubai hills': 11000,
  'city walk': 13000,
  'dubai silicon oasis': 5000,
  'international city': 4000,
  'jvc': 6000,
  'motor city': 7000,
  'sports city': 6500,
  'remraam': 5500,
  'town square': 6000,
  'arjan': 5500,
  'mirdif': 4500,
  'deira': 3500,
  'bur dubai': 4000,
  'jumeirah': 12000,
  'umm suqeim': 10000,
  'al barsha': 8000,
  'jumeirah village circle': 6000,
  'jumeirah village triangle': 5500,
  'dubai land': 5000,
  'dubai production city': 4500,
  'dubai investment park': 4000,
  'dubai south': 3500,
  'dubai hills estate': 11000,
  'emirates hills': 20000,
  'springs': 8000,
  'meadows': 9000,
  'lakes': 10000,
  'arabian ranches': 12000,
  'damac hills': 7000,
  'damac hills 2': 6000,
  'damac hills 3': 5500,
  'damac hills 4': 5000,
  'damac hills 5': 4500,
  'damac hills 6': 4000,
  'damac hills 7': 3500,
  'damac hills 8': 3000,
  'damac hills 9': 2500,
  'damac hills 10': 2000,
  'damac hills 11': 1500,
  'damac hills 12': 1000,
  'damac hills 13': 500,
  'damac hills 14': 100,
  'damac hills 15': 50,
  'damac hills 16': 25,
  'damac hills 17': 10,
  'damac hills 18': 5,
  'damac hills 19': 1,
  'damac hills 20': 0.5,
  'damac hills 21': 0.1,
  'damac hills 22': 0.05,
  'damac hills 23': 0.01,
  'damac hills 24': 0.005,
  'damac hills 25': 0.001,
  'damac hills 26': 0.0005,
  'damac hills 27': 0.0001,
  'damac hills 28': 0.00005,
  'damac hills 29': 0.00001,
  'damac hills 30': 0.000005,
  'damac hills 31': 0.000001,
  'damac hills 32': 0.0000005,
  'damac hills 33': 0.0000001,
  'damac hills 34': 0.00000005,
  'damac hills 35': 0.00000001,
  'damac hills 36': 0.000000005,
  'damac hills 37': 0.000000001,
  'damac hills 38': 0.0000000005,
  'damac hills 39': 0.0000000001,
  'damac hills 40': 0.00000000005,
  'damac hills 41': 0.00000000001,
  'damac hills 42': 0.000000000005,
  'damac hills 43': 0.000000000001,
  'damac hills 44': 0.0000000000005,
  'damac hills 45': 0.0000000000001,
  'damac hills 46': 0.00000000000005,
  'damac hills 47': 0.00000000000001,
  'damac hills 48': 0.000000000000005,
  'damac hills 49': 0.000000000000001,
  'damac hills 50': 0.0000000000000005,
  'damac hills 51': 0.0000000000000001,
  'damac hills 52': 0.00000000000000005,
  'damac hills 53': 0.00000000000000001,
  'damac hills 54': 0.000000000000000005,
  'damac hills 55': 0.000000000000000001,
  'damac hills 56': 0.0000000000000000005,
  'damac hills 57': 0.0000000000000000001,
  'damac hills 58': 0.00000000000000000005,
  'damac hills 59': 0.00000000000000000001,
  'damac hills 60': 0.000000000000000000005,
  'damac hills 61': 0.000000000000000000001,
  'damac hills 62': 0.0000000000000000000005,
  'damac hills 63': 0.0000000000000000000001,
  'damac hills 64': 0.00000000000000000000005,
  'damac hills 65': 0.00000000000000000000001,
  'damac hills 66': 0.000000000000000000000005,
  'damac hills 67': 0.000000000000000000000001,
  'damac hills 68': 0.0000000000000000000000005,
  'damac hills 69': 0.0000000000000000000000001,
  'damac hills 70': 0.00000000000000000000000005,
  'damac hills 71': 0.00000000000000000000000001,
  'damac hills 72': 0.000000000000000000000000005,
  'damac hills 73': 0.000000000000000000000000001,
  'damac hills 74': 0.0000000000000000000000000005,
  'damac hills 75': 0.0000000000000000000000000001,
  'damac hills 76': 0.00000000000000000000000000005,
  'damac hills 77': 0.00000000000000000000000000001,
  'damac hills 78': 0.000000000000000000000000000005,
  'damac hills 79': 0.000000000000000000000000000001,
  'damac hills 80': 0.0000000000000000000000000000005,
  'damac hills 81': 0.0000000000000000000000000000001,
  'damac hills 82': 0.00000000000000000000000000000005,
  'damac hills 83': 0.00000000000000000000000000000001,
  'damac hills 84': 0.000000000000000000000000000000005,
  'damac hills 85': 0.000000000000000000000000000000001,
  'damac hills 86': 0.0000000000000000000000000000000005,
  'damac hills 87': 0.0000000000000000000000000000000001,
  'damac hills 88': 0.00000000000000000000000000000000005,
  'damac hills 89': 0.00000000000000000000000000000000001,
  'damac hills 90': 0.000000000000000000000000000000000005,
  'damac hills 91': 0.000000000000000000000000000000000001,
  'damac hills 92': 0.0000000000000000000000000000000000005,
  'damac hills 93': 0.0000000000000000000000000000000000001,
  'damac hills 94': 0.00000000000000000000000000000000000005,
  'damac hills 95': 0.00000000000000000000000000000000000001,
  'damac hills 96': 0.000000000000000000000000000000000000005,
  'damac hills 97': 0.000000000000000000000000000000000000001,
  'damac hills 98': 0.0000000000000000000000000000000000000005,
  'damac hills 99': 0.0000000000000000000000000000000000000001,
  'damac hills 100': 0.00000000000000000000000000000000000000005,
  'default': 6000
};

// Property type multipliers
const PROPERTY_TYPE_MULTIPLIERS: { [key: string]: number } = {
  'studio': 0.7,
  'apartment': 1.0,
  'villa': 1.8,
  'townhouse': 1.4,
  'penthouse': 2.5,
  'other': 1.0
};

// Furnishing status multipliers
const FURNISHING_MULTIPLIERS: { [key: string]: number } = {
  'furnished': 1.0,
  'semi-furnished': 0.8,
  'unfurnished': 0.6
};

// Bedroom multipliers
const BEDROOM_MULTIPLIERS: { [key: string]: number } = {
  'studio': 0.7,
  '1': 1.0,
  '2': 1.4,
  '3': 1.8,
  '4': 2.2,
  '5': 2.6,
  '6': 3.0,
  '7': 3.4,
  '7+': 3.8
};

// Bathroom multipliers
const BATHROOM_MULTIPLIERS: { [key: string]: number } = {
  '1': 1.0,
  '2': 1.2,
  '3': 1.4,
  '4': 1.6,
  '5': 1.8,
  '6': 2.0,
  '7': 2.2,
  '7+': 2.4
};

export function calculatePropertyPrediction(inputs: PropertyInputs): PropertyPrediction {
  // Get base rate for area
  const areaKey = inputs.area.toLowerCase().trim();
  const baseRate = AREA_RATES[areaKey] || AREA_RATES['default'];
  
  // Calculate multipliers
  const propertyTypeMultiplier = PROPERTY_TYPE_MULTIPLIERS[inputs.propertyType.toLowerCase()] || 1.0;
  const furnishingMultiplier = FURNISHING_MULTIPLIERS[inputs.furnishStatus.toLowerCase()] || 1.0;
  
  // Calculate bedroom multiplier (use highest selected or average)
  const bedroomMultiplier = inputs.selectedBeds.length > 0 
    ? Math.max(...inputs.selectedBeds.map(bed => BEDROOM_MULTIPLIERS[bed] || 1.0))
    : 1.0;
  
  // Calculate bathroom multiplier (use highest selected or average)
  const bathroomMultiplier = inputs.selectedBaths.length > 0
    ? Math.max(...inputs.selectedBaths.map(bath => BATHROOM_MULTIPLIERS[bath] || 1.0))
    : 1.0;
  
  // Calculate base monthly income
  const baseMonthlyIncome = baseRate * propertyTypeMultiplier * furnishingMultiplier * bedroomMultiplier * bathroomMultiplier;
  
  // Apply occupancy rate (85% average for Dubai holiday homes)
  const occupancyRate = 0.85;
  const estimatedMonthlyIncome = Math.round(baseMonthlyIncome * occupancyRate);
  const estimatedYearlyIncome = estimatedMonthlyIncome * 12;
  
  // Calculate confidence based on input completeness
  let confidence: 'low' | 'medium' | 'high' = 'low';
  if (inputs.area && inputs.propertyType && inputs.furnishStatus && inputs.selectedBeds.length > 0) {
    confidence = 'high';
  } else if (inputs.area && (inputs.propertyType || inputs.furnishStatus)) {
    confidence = 'medium';
  }
  
  // Generate recommendations
  const recommendations = generateRecommendations(inputs, estimatedMonthlyIncome);
  
  // Calculate market insights
  const marketInsights = {
    averageRentalRate: Math.round(baseMonthlyIncome),
    occupancyRate: Math.round(occupancyRate * 100),
    seasonalVariation: 15 // 15% seasonal variation
  };
  
  return {
    estimatedMonthlyIncome,
    estimatedYearlyIncome,
    confidence,
    factors: {
      location: Math.round(baseRate),
      propertyType: Math.round(propertyTypeMultiplier * 100) / 100,
      furnishing: Math.round(furnishingMultiplier * 100) / 100,
      bedrooms: Math.round(bedroomMultiplier * 100) / 100,
      bathrooms: Math.round(bathroomMultiplier * 100) / 100
    },
    recommendations,
    marketInsights
  };
}

function generateRecommendations(inputs: PropertyInputs, estimatedIncome: number): string[] {
  const recommendations: string[] = [];
  
  // Location-based recommendations
  if (inputs.area.toLowerCase().includes('downtown') || inputs.area.toLowerCase().includes('marina')) {
    recommendations.push("Premium location! Consider luxury furnishing to maximize returns.");
  } else if (inputs.area.toLowerCase().includes('silicon') || inputs.area.toLowerCase().includes('international')) {
    recommendations.push("Budget-friendly area. Focus on modern amenities to attract guests.");
  }
  
  // Property type recommendations
  if (inputs.propertyType.toLowerCase() === 'villa') {
    recommendations.push("Villas have high earning potential. Consider adding a pool or garden area.");
  } else if (inputs.propertyType.toLowerCase() === 'studio') {
    recommendations.push("Studio apartments work best in central locations. Optimize space efficiently.");
  }
  
  // Furnishing recommendations
  if (inputs.furnishStatus.toLowerCase() === 'unfurnished') {
    recommendations.push("Furnishing your property could increase income by 40-60%.");
  } else if (inputs.furnishStatus.toLowerCase() === 'semi-furnished') {
    recommendations.push("Complete furnishing could boost your rental income significantly.");
  }
  
  // Income-based recommendations
  if (estimatedIncome > 15000) {
    recommendations.push("High-value property! Consider professional photography and premium amenities.");
  } else if (estimatedIncome < 5000) {
    recommendations.push("Consider location improvements or property upgrades to increase value.");
  }
  
  // General recommendations
  recommendations.push("Professional property management can increase occupancy by 20-30%.");
  recommendations.push("Regular maintenance and guest reviews are crucial for success.");
  
  return recommendations.slice(0, 4); // Limit to 4 recommendations
}

// Helper function to get area suggestions
export function getAreaSuggestions(query: string): string[] {
  const areas = Object.keys(AREA_RATES).filter(area => 
    area !== 'default' && area.toLowerCase().includes(query.toLowerCase())
  );
  return areas.slice(0, 5); // Return top 5 matches
}

// Helper function to validate inputs
export function validateInputs(inputs: PropertyInputs): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!inputs.area.trim()) {
    errors.push("Area is required");
  }
  
  if (!inputs.propertyType) {
    errors.push("Property type is required");
  }
  
  if (!inputs.furnishStatus) {
    errors.push("Furnish status is required");
  }
  
  if (inputs.selectedBeds.length === 0) {
    errors.push("At least one bedroom option must be selected");
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}
